import {api, RequestParam} from "@/shared/api";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {showPdfPreview} from "@/features/pdfPreview/showPdfPreview";
import {confirmExportOfEmptyTable} from "./confirmExportOfEmptyTable";
import {RecordingValidationTable} from "@/features/reports/recordingValidationTable";
import {TimeNavigationBar} from "@/shared/ui/timeNavigationBar";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {showBulkOperationResult} from "@/shared/bulkOperationHelper";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {Combobox} from "@/shared/ui/combobox";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {ProjectTimeDetailsDialog} from "@/features/projecttime/projectTimeDetailsDialog";
import {WorkingTimeDetailsDialog} from "@/features/workingtime/workingTimeDetailsDialog";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import * as Constants from "@/shared/constants";
import {getWorkingTimeTypesForAddAbsence, getWorkingTimeTypesForAddWorkingTime} from "@/shared/securityHelper";
import * as RecordingValidationUtil from "@/shared/validations/recordingValidationUtil";
import {BulkSelectorContextMenu} from "@/shared/ui/bulkSelectorContextMenu";
import {DataTableBulkSelector} from "@/shared/ui/dataTableBulkSelector";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {getISODate, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {
	BulkOperationResult,
	CurrentlyGeneratedMessageResponse,
	CustomFieldDefinition,
	FilterUsersResponse,
	Group,
	User,
	ValidationResult,
	WorkingTimeType
} from "@/shared/timr";
import {
	AggregateRecordingValidationCategory,
	DateTimeChangeableByUserDayRangeMode,
	ExportType,
	ProjectTimeValidationCategory,
	RecordingValidationsManagerRoleFilter,
	RecordingValidationsMode,
	RecordingValidationStatus,
	TimeNavigationType,
	ValidationSeverity,
	WorkingTimeValidationCategory
} from "@/shared/timrTypes";

interface RecordingValidationsConfig {
	users: User[];
	workingTimeTypes: WorkingTimeType[];
	customFieldDefinitionsWorkingTime: CustomFieldDefinition[];
	customFieldDefinitionsProjectTime: CustomFieldDefinition[];
	isGroupLeaderForThemself: boolean;
	workingTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	workingTimeDateTimeChangeableByUserDayRangeUntil: string | null;
	projectTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	projectTimeDateTimeChangeableByUserDayRangeUntil: string | null;
}

type RecordingValidationCategory = WorkingTimeValidationCategory | ProjectTimeValidationCategory;

export class RecordingValidationsPage {
	private dom = $("#recordingValidationsContent");
	private config: RecordingValidationsConfig;

	private timeNavigationBar: TimeNavigationBar;
	private modeControl: ButtonRadioGroup<RecordingValidationsMode>;
	private roleFilterControl?: ButtonRadioGroup<RecordingValidationsManagerRoleFilter>;
	private groupDropdown: GroupDropdown;
	private userCombobox: Combobox<User>;

	private recordingValidationTable: RecordingValidationTable;
	private projectTimeDialog: ProjectTimeDialog;
	private projectTimeDetailsDialog: ProjectTimeDetailsDialog;
	private workingTimeDialog: WorkingTimeDialog;
	private workingTimeDetailsDialog: WorkingTimeDetailsDialog;

	private currentlyGeneratedMessageRequest: JQuery.jqXHR;
	private userFilterInitialized = false;

	constructor(config: RecordingValidationsConfig) {
		this.config = config;
		this.initFilterPanel();
		this.initTimeNavigationBar();
		this.initTable();
		this.initDialogs();
		this.initListeners();

		// set mode from session settings if available
		const mode: RecordingValidationsMode = BrowserStorageSettings.getRecordingValidationsMode(Constants.GROUP_RECORDING_VALIDATIONS);
		if (mode) {
			this.modeControl.setValue((mode === RecordingValidationsMode.Manager && !globals.isGroupLeader && !globals.isTaskLeader) ? RecordingValidationsMode.Personal : mode);
		} else {
			this.modeControl.setValue((globals.isGroupLeader || globals.isTaskLeader) ? RecordingValidationsMode.Manager : RecordingValidationsMode.Personal);
		}
	}

	private initListeners() {
		this.dom.find("#recordingValidationsFilterForm select").change(() => this.filterTable());
		this.dom.find("#recordingValidationExportDropDown a").click(e => {
			const link = $(e.currentTarget);
			confirmExportOfEmptyTable(this.recordingValidationTable.getRowCount(), () => link.data("preview")
				? this.previewValidations()
				: this.exportValidations(link.data("exportType") as ExportType));
		});
		this.dom.find("#showWithTimeAccount").change(() => this.filterTable());
		this.dom.find(".reload-table").click(() => this.reloadTable());

		const statusSelect: JQuery = this.dom.find("#recordingValidationsStatusSelect");
		statusSelect.change(() => BrowserStorageSettings.setRecordingValidationsStatus(Constants.GROUP_RECORDING_VALIDATIONS, statusSelect.val() as RecordingValidationStatus));

		const severitySelect: JQuery = this.dom.find("#severitySelect");
		severitySelect.change(() => BrowserStorageSettings.setRecordingValidationsSeverity(Constants.GROUP_RECORDING_VALIDATIONS, severitySelect.val() as ValidationSeverity));

		const showInactiveUsersCheckbox: JQuery = this.dom.find("#recordingValidationsShowInactive");
		showInactiveUsersCheckbox.change(() => BrowserStorageSettings.setReportingShowInactiveUsers(Constants.GROUP_RECORDING_VALIDATIONS, showInactiveUsersCheckbox.is(":checked")));
	}

	private initAlert() {
		this.currentlyGeneratedMessageRequest?.abort();

		this.currentlyGeneratedMessageRequest = api("reports/getRecordingValidationsCurrentlyGeneratedMessage.json")
			.get({
				groupId: this.groupDropdown.getSelectedId(),
				userId: this.getUserId(),
				showInactive: this.modeControl.getValue() === RecordingValidationsMode.Manager ? this.getShowInactiveValue() : "false",
				mode: this.modeControl.getValue(),
				roleFilter: this.modeControl.getValue() === RecordingValidationsMode.Manager ? this.roleFilterControl?.getValue() : undefined
			})
			.done((data: CurrentlyGeneratedMessageResponse) => {
				const alertBox = this.dom.find("#recordingValidationsCurrentlyGenerated");
				if (data?.message) {
					alertBox.html(`<span>${data.message}</span>`);
					alertBox.show();
				} else {
					alertBox.html("<span/>");
					alertBox.hide();
				}
			});
	}

	//  if only one export type is available, the code is partially not really necessary
	private exportValidations(exportType: ExportType) {
		void this.requestExport(exportType).then(exportJobId => new DownloadStatus({
			exportJobId,
			exportStatusBaseUrl: "export/status.json",
			exportType: exportType,
			loadingMessage: messages["downloadStatus.loadingMessage.reports"],
			allowCancellation: true
		}));
	}

	private previewValidations() {
		void this.requestExport(ExportType.Pdf).then(exportJobId => showPdfPreview(messages["reports.export.preview"], exportJobId, messages["downloadStatus.loadingMessage.reports"]));
	}

	/** Starts the export job and resolves with its id; never resolves if no job was started. */
	private requestExport(exportType: ExportType): Promise<string> {
		const params = this.buildFilterParams();
		params.push({name: "exportFileType", value: exportType});
		params.push({name: "idsInclude", value: this.recordingValidationTable.bulkSelector.getIdsToInclude()?.join(",")});
		params.push({name: "idsExclude", value: this.recordingValidationTable.bulkSelector.getIdsToExclude()?.join(",")});
		params.push({name: "allSelected", value: this.recordingValidationTable.bulkSelector.isAllSelected()});

		return new Promise(resolve => {
			void api("export/recordingValidations.json")
				.post(params)
				.done(data => {
					if (data.exportJobId) {
						resolve(data.exportJobId);
					}
				});
		});
	}

	private initTimeNavigationBar() {
		this.timeNavigationBar = new TimeNavigationBar({
			container: $(".breadcrumb-line-dashboard"),
			timeSpanChanged: (from, to, type) => {
				BrowserStorageSettings.setReportingDateFrom(Constants.GROUP_RECORDING_VALIDATIONS, from);
				BrowserStorageSettings.setReportingDateTo(Constants.GROUP_RECORDING_VALIDATIONS, to);
				BrowserStorageSettings.setReportingDateType(Constants.GROUP_RECORDING_VALIDATIONS, type);
				this.filterTable();
			},
			defaultDateType: BrowserStorageSettings.getReportingDateType(Constants.GROUP_RECORDING_VALIDATIONS),
			defaultFromDate: BrowserStorageSettings.getReportingDateFrom(Constants.GROUP_RECORDING_VALIDATIONS),
			defaultToDate: BrowserStorageSettings.getReportingDateTo(Constants.GROUP_RECORDING_VALIDATIONS),
		});

		// use year as default if no session variable with date type is set
		const reportingDateType = BrowserStorageSettings.getReportingDateType(Constants.GROUP_RECORDING_VALIDATIONS);
		if (reportingDateType === null) {
			const now = Temporal.Now.plainDateISO();
			this.timeNavigationBar.setTimeSpan(
				now.with({month: 1, day: 1}),
				now.with({month: 12, day: 31}),
				TimeNavigationType.Year
			);
		}
	}

	protected filterUsersCombobox(userCombobox: Combobox<User>, selectedGroupId: number, roleFilter?: RecordingValidationsManagerRoleFilter) {
		api("reports/filterRecordingValidationUsers.json")
			.get({
				groupId: selectedGroupId,
				activeUsersOnly: !this.getShowInactiveValue(),
				roleFilter: roleFilter ?? this.roleFilterControl?.getValue()
			})
			.done((data: FilterUsersResponse) => {
				if (data?.users) {
					userCombobox.updateItems(data.users);

					if (!this.userFilterInitialized && (globals.isGroupLeader || globals.isTaskLeader)) {
						// set selected userId in the user filter for group / task leader
						userCombobox.setSelectedItemById(BrowserStorageSettings.getReportUserFilter(Constants.GROUP_REPORT_FILTER));
						this.userFilterInitialized = true;
					}
				}
			});
	}

	private initFilterPanel() {
		const initialRoleFilter = (globals.isGroupLeader && globals.isTaskLeader)
			? BrowserStorageSettings.getRecordingValidationsRoleFilter(Constants.GROUP_RECORDING_VALIDATIONS) ?? RecordingValidationsManagerRoleFilter.ALL
			: undefined;

		this.initGroupUserSelection(initialRoleFilter);
		this.modeControl = ButtonRadioGroup.typed({
			value: RecordingValidationsMode.Personal,
			button: document.getElementById("recordingValidationModePersonalButton")
		}, {
			value: RecordingValidationsMode.Manager,
			button: document.getElementById("recordingValidationModeManagerButton")
		}).onValueChanged((mode: RecordingValidationsMode) => this.setMode(mode));

		if (globals.isGroupLeader && globals.isTaskLeader) {
			this.roleFilterControl = ButtonRadioGroup.typed({
				value: RecordingValidationsManagerRoleFilter.ALL,
				button: document.getElementById("roleFilterAll")
			}, {
				value: RecordingValidationsManagerRoleFilter.TEAM_LEADER,
				button: document.getElementById("roleFilterTeamLeader")
			}, {
				value: RecordingValidationsManagerRoleFilter.TASK_LEADER,
				button: document.getElementById("roleFilterTaskLeader")
			}).onValueChanged((roleFilter: RecordingValidationsManagerRoleFilter) => {
				BrowserStorageSettings.setRecordingValidationsRoleFilter(Constants.GROUP_RECORDING_VALIDATIONS, roleFilter);
				this.filterUsersCombobox(this.userCombobox, this.groupDropdown.getSelectedId(), roleFilter);
				this.filterTable();
			});

			this.roleFilterControl.setValue(BrowserStorageSettings.getRecordingValidationsRoleFilter(Constants.GROUP_RECORDING_VALIDATIONS) ?? RecordingValidationsManagerRoleFilter.ALL);
		}

		// initialize bootstrap-multiselect input for validation categories
		const categories = BrowserStorageSettings.getRecordingValidationsCategories(Constants.GROUP_RECORDING_VALIDATIONS)?.split(",");
		this.dom.find("#categoriesSelect")
			.multiselect({
				includeSelectAllOption: true,
				numberDisplayed: 1, // one selected item is displayed by text, another ones by 'n selected' in the input field
				selectAllText: messages["filter.all"],
				allSelectedText: messages["filter.all"],
				nonSelectedText: messages["filter.all"],
				nSelectedText: messages["filter.select.n"],
				selectedClass: "", // selected elements are only visualized by checkmark (not by extra class)
				buttonWidth: "100%", // input field should have the full length as parent container
				enableFiltering: true,
				enableCaseInsensitiveFiltering: true,
				filterPlaceholder: messages["filter.search"],
				onSelectAll: () => this.multiSelectOnChange(),
				onInitialized: (_select, container) => {
					// Update style to be consistent with other select inputs
					$(".multiselect-clear-filter > i").removeClass().addClass("fas fa-times");
					// Set min-width to avoid jumping of the layout while searching
					$(".multiselect-container.dropdown-menu").css("min-width", "725px");
				},
				onDropdownShown: () => {
					$(".multiselect-search").focus();
				},
				onChange: () => this.multiSelectOnChange()
			});

		categories?.forEach((category: string) => {
			const checkbox = $(`[value="${category}"]`);
			checkbox.prop("checked", true);
			checkbox.change();
		});

		const status = BrowserStorageSettings.getRecordingValidationsStatus(Constants.GROUP_RECORDING_VALIDATIONS);
		if (status) {
			$("#recordingValidationsStatusSelect").val(status);
		}

		const severity = BrowserStorageSettings.getRecordingValidationsSeverity(Constants.GROUP_RECORDING_VALIDATIONS);
		if (severity) {
			$("#severitySelect").val(severity);
		}

		const showInactiveUsers = BrowserStorageSettings.getReportingShowInactiveUsers(Constants.GROUP_RECORDING_VALIDATIONS);
		$("#recordingValidationsShowInactive").prop("checked", showInactiveUsers);
		this.filterUsersCombobox(this.userCombobox, this.groupDropdown.getSelectedId());
	}

	/**
	 * Sets the mode of the page. Based on this mode the page displays different elements:
	 * Personal: Own validations, Acknowledge button
	 * Manager: Team validations, (Group and) User filter, Confirm button
	 * @param mode
	 * @private
	 */
	private setMode(mode: RecordingValidationsMode) {
		this.recordingValidationTable.setManagerMode(mode);
		BrowserStorageSettings.setRecordingValidationsMode(Constants.GROUP_RECORDING_VALIDATIONS, mode);

		const managerMode = mode === RecordingValidationsMode.Manager;
		this.dom.find("#recordingValidationsShowInactive")
			.closest(".checkbox-inline")
			.css("visibility", managerMode ? "visible" : "hidden") // toggle inactive checkbox with "visibility" to avoid jumping content
			.find("input[type='checkbox']").toggle(managerMode); // toggle checker with "display" otherwise there is a delay for it
		this.dom.find(".managerMode").toggle(managerMode);
		this.filterTable();
		this.initAlert();
	}

	private initGroupUserSelection(initialRoleFilter?: RecordingValidationsManagerRoleFilter) {
		this.userCombobox = new Combobox<User>({
			items: [],
			formatter: user => user.fullname,
			idElement: $("#recordingValidationsFilterUserId"),
			filterElement: $("#recordingValidationsFilterUserFilter"),
			caretElement: $("#recordingValidationsFilterUserCaret"),
			emptyMessage: messages["user.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});

		$(this.userCombobox).on(Combobox.EVENT_CHANGED, () => {
			const userId = this.userCombobox.getSelectedItem()?.id;
			BrowserStorageSettings.setReportUserFilter(Constants.GROUP_REPORT_FILTER, userId);
			this.filterTable();
			this.initAlert();
		});
		this.groupDropdown = new GroupDropdown({
			rootElement: $("#recordingValidationsFilterGroupDropdown"),
			allowSelectionOfNotAssigned: false,
			emptyMessage: messages["group.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		const defaultGroup = BrowserStorageSettings.getTimeAccountGroupFilter(Constants.GROUP_REPORT_FILTER);
		if (defaultGroup) {
			this.groupDropdown.setSelection(defaultGroup.id, defaultGroup.name, defaultGroup.breadCrumbs, defaultGroup);
		}
		this.filterUsersCombobox(this.userCombobox, defaultGroup?.id, initialRoleFilter);
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group: Group) => {
			BrowserStorageSettings.setTimeAccountGroupFilter(Constants.GROUP_REPORT_FILTER, group);
			this.filterUsersCombobox(this.userCombobox, group?.id);
			this.filterTable();
			this.initAlert();
		});
		$("#recordingValidationsShowInactive").change(() => {
			this.filterUsersCombobox(this.userCombobox, this.groupDropdown.getSelectedId());
			this.filterTable();
			this.initAlert();
		});
	}

	private initTable() {
		this.recordingValidationTable = new RecordingValidationTable({
			element: $("#recordingValidationTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildFilterParams(),
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			allowSelect: true,
			selectCountElement: this.dom.find("[data-selected-rows-count]"),
			workingTimeTypes: this.config.workingTimeTypes
		});
		this.recordingValidationTable.getDataTable().on("init.dt", () => {
			this.dom.find("#recordingValidationExportButton").show();
			$(".reportExportButtonLoadingIndicator").hide();
		});
		this.recordingValidationTable.getDataTable().on("processing.dt", (e, settings, processing) => {
			if (processing) {
				Button.setLoading(this.dom.find("#recordingValidationExportButton"));
			} else {
				Button.resetLoading(this.dom.find("#recordingValidationExportButton"));
			}
		});

		this.recordingValidationTable.getDataTable()
			.on(RecordingValidationTable.EVENT_ADD_WORKTIME, (e, validation: ValidationResult, start: Temporal.PlainDateTime, end: Temporal.PlainDateTime) => {
				this.workingTimeDialog.create({userId: validation.user.id}, false, getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes));
				this.workingTimeDialog.setStartEndTime(
					start?.toZonedDateTime(Temporal.Now.timeZoneId()),
					end?.toZonedDateTime(Temporal.Now.timeZoneId())
				);
			})
			.on(RecordingValidationTable.EVENT_ADD_ABSENCE, (e, validation: ValidationResult, start: Temporal.PlainDateTime, end: Temporal.PlainDateTime) => {
				this.workingTimeDialog.create({userId: validation.user.id}, false, getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes));
				this.workingTimeDialog.setStartEndTime(
					start?.toZonedDateTime(Temporal.Now.timeZoneId()),
					end?.toZonedDateTime(Temporal.Now.timeZoneId())
				);
			});
		this.handleBulkOperationContextMenu();
		$(this.recordingValidationTable.bulkSelector).on(DataTableBulkSelector.EVENT_BULK_SELECTION_CHANGED, () => {
			// bulk operation context button should be only visible if selected entries
			this.dom.find("#recordingValidationBulkOperationButton").toggle(this.recordingValidationTable.bulkSelector.hasSelectedEntries());
		});
	}

	private buildFilterParams(): RequestParam[] {
		// contains status, severity, group
		const params: RequestParam[] = this.dom.find("#recordingValidationsFilterForm").serializeArray();

		// add start, end, user to filter
		params.push({name: "startDate", value: getISODate(this.timeNavigationBar.getDateFrom())});
		params.push({name: "endDate", value: getISODate(this.timeNavigationBar.getDateTo())});

		// if personal mode is selected inputs are invisible and current user id has to be transmitted
		params.push({name: "userId", value: this.getUserId()});

		const selectedCategories = Array.from(document.querySelector<HTMLSelectElement>("#categoriesSelect").selectedOptions);

		const aggregateCategories = selectedCategories
			.filter(o => o.dataset.categoryType === "aggregate")
			.map(o => o.value as AggregateRecordingValidationCategory);

		const individualCategories = selectedCategories
			.filter(o => o.dataset.categoryType === "individual")
			.map(o => o.value as RecordingValidationCategory);

		// Send individual and aggregate categories separately (backend resolves aggregate categories to individual categories)
		params.push({name: "categories", value: individualCategories.join(",")});
		params.push({name: "aggregateCategories", value: aggregateCategories.join(",")});

		// show inactive users if checkbox is checked (always false for personal mode)
		params.push({
			name: "showInactive",
			value: this.modeControl.getValue() === RecordingValidationsMode.Manager ? this.getShowInactiveValue() : false
		});
		params.push({name: "mode", value: this.modeControl.getValue()});

		if (this.modeControl.getValue() === RecordingValidationsMode.Manager && this.roleFilterControl) {
			params.push({name: "roleFilter", value: this.roleFilterControl.getValue()});
		}

		return params;
	}

	private initDialogs() {
		$("#recordingValidationTable")
			.on("click", ".recordWorkingTime[data-id]", (e) => {
				const link = $(e.target as HTMLElement).closest(".recordWorkingTime");
				this.getWorkingTimeById(Number(link.attr("data-id")), link);
			})
			.on("click", ".recordProjectTime[data-id]", (e) => {
				const link = $(e.target as HTMLElement).closest(".recordProjectTime");
				this.getProjectTimeById(Number(link.attr("data-id")), link);
			})
			.on("click", "[data-user-trust-based]", () => {
				BootstrapModal.trustBasedNoAccess();
			});

		// DEVNOTE TIMR-11701: keep allowedUserIds in sync with the dialog users.
		// If this array is empty (or misses the selected user), date/time editing is denied for
		// "Changeable by teamleader" and the start/end fields are disabled in the dialog.
		// Example:
		//   isWorkingTimeDateTimeChangeAllowed(false, selectedUserId, []) === false
		//   -> recordDialog.configureDateTimeFields(false) -> start/end inputs disabled
		const allowedUserIds = this.config.users.map(it => it.id);

		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			users: this.config.users,
			userSelectionEnabled: true,
			customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime,
			defaultStatusNumber: null,
			allowedUserIds: allowedUserIds,
			isGroupLeaderForThemself: false,
			dateTimeChangeableByUserDayRangeMode: this.config.workingTimeDateTimeChangeableByUserDayRangeMode,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.workingTimeDateTimeChangeableByUserDayRangeUntil)
		});
		$(this.workingTimeDialog)
			.on(WorkingTimeDialog.EVENT_RECORD_EDITED, () => this.filterTable())
			.on(WorkingTimeDialog.EVENT_RECORD_CREATED, () => this.filterTable())
			.on(WorkingTimeDialog.EVENT_REQUEST_CREATED, () => this.filterTable())
			.on(WorkingTimeDialog.EVENT_RECORD_DELETED, () => this.filterTable());

		this.projectTimeDialog = new ProjectTimeDialog({
			dialogId: "#projectTimeDialog",
			users: this.config.users,
			userSelectionEnabled: true,
			customFieldDefinitions: this.config.customFieldDefinitionsProjectTime,
			defaultStatusNumber: null,
			allowedUserIds: allowedUserIds,
			isGroupLeaderForThemself: false,
			dateTimeChangeableByUserDayRangeMode: this.config.projectTimeDateTimeChangeableByUserDayRangeMode,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.projectTimeDateTimeChangeableByUserDayRangeUntil)
		});
		$(this.projectTimeDialog)
			.on(ProjectTimeDialog.EVENT_RECORD_EDITED, () => this.filterTable())
			.on(ProjectTimeDialog.EVENT_RECORD_DELETED, () => this.filterTable());

		this.workingTimeDetailsDialog = new WorkingTimeDetailsDialog({
			title: messages["workingTime.details.header"],
			dialogId: "#workingTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime,
			editRecord: (workingTime) => this.workingTimeDialog.edit(workingTime)
		});

		this.projectTimeDetailsDialog = new ProjectTimeDetailsDialog({
			title: messages["projectTime.details.header"],
			dialogId: "#projectTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitionsProjectTime,
			editRecord: (projectTime) => this.projectTimeDialog.edit(projectTime)
		});
	}

	protected handleBulkOperationContextMenu() {
		const button = this.dom.find("#recordingValidationBulkOperationButton");
		const bulkSelectorContextMenu = this.createBulkMenu();
		const dropdownMenu = button.closest(".dropdown").find(".dropdown-menu");
		if (dropdownMenu.length !== 0) {
			dropdownMenu.remove();
		}
		button.after(bulkSelectorContextMenu.render());
		bulkSelectorContextMenu.registerOnButton(button);
	}

	protected createBulkMenu() {
		const menu = new BulkSelectorContextMenu(false, true);
		menu.addMenuEntry({
			label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.OPEN"]),
			action: () => this.bulkSetStatus(RecordingValidationStatus.OPEN)
		}).addMenuEntry({
			label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.ACKNOWLEDGED"]),
			action: () => this.bulkSetStatus(RecordingValidationStatus.ACKNOWLEDGED)
		})
			.addMenuEntry({
				label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.CONFIRMED"]),
				action: () => this.bulkSetStatus(RecordingValidationStatus.CONFIRMED),
				show: () => this.modeControl.getValue() === RecordingValidationsMode.Manager
			})
			.addMenuEntry({
				label: messages.withArguments("recordingValidation.context.setStatus", messages["recordingValidation.status.CLOSED"]),
				action: () => this.bulkSetStatus(RecordingValidationStatus.CLOSED),
				show: () => this.modeControl.getValue() === RecordingValidationsMode.Manager
			});

		return menu;
	}

	/**
	 * Triggers request to change the status of the selected validations<p>
	 * There is a response result object that contains values whether the status of all objects was changed correctly<br>
	 * If not everything has been updated, a dialog will open with information about it
	 * @param newStatus to which the selected validations should be changed
	 */
	public bulkSetStatus(newStatus: RecordingValidationStatus) {
		const params = this.buildFilterParams();

		// add params for bulk select
		params.push({name: "idsInclude", value: this.recordingValidationTable.bulkSelector.getIdsToInclude()?.join(",")});
		params.push({name: "idsExclude", value: this.recordingValidationTable.bulkSelector.getIdsToExclude()?.join(",")});
		params.push({name: "allSelected", value: this.recordingValidationTable.bulkSelector.isAllSelected()});
		params.push({name: "newStatus", value: newStatus});

		void api("reports/bulkSetRecordingValidationsStatus.json")
			.post(params)
			.done((result: BulkOperationResult) => {
				statusElementsHolder.updateStatus();
				if (!result.allUpdated) {
					showBulkOperationResult(result, "validation.bulkStatusChange.");
				}
				this.reloadTable();
			});
	}

	private getShowInactiveValue(): boolean {
		return $("#recordingValidationsShowInactive").is(":checked");
	}

	protected getWorkingTimeById(workingTimeRecordId: number, link: JQuery) {
		RecordingValidationUtil.setRecordingValidationLinkIconLoading(link);
		void api("reports/getWorkingTime.json")
			.get({workingTimeId: workingTimeRecordId})
			.done(workingTime => this.workingTimeDetailsDialog.show(workingTime))
			.always(() => RecordingValidationUtil.resetRecordingValidationLinkIconLoading(link));
	}

	protected getProjectTimeById(projectTimeRecordId: number, link: JQuery) {
		RecordingValidationUtil.setRecordingValidationLinkIconLoading(link);
		void api("reports/getProjectTime.json")
			.get({projectTimeId: projectTimeRecordId})
			.done(projectTime => this.projectTimeDetailsDialog.show(projectTime))
			.always(() => RecordingValidationUtil.resetRecordingValidationLinkIconLoading(link));
	}

	/**
	 * Filters table with selected filter params including status, severity, group, start, end, user
	 * @private
	 */
	private filterTable() {
		// table is optional because while the initialization it is not set
		void this.recordingValidationTable?.filter(this.buildFilterParams());
	}

	/**
	 * Reloads table
	 * @private
	 */
	private reloadTable() {
		// table is optional because while the initialization it is not set
		void this.recordingValidationTable?.reload();
	}

	/**
	 * Returns user id. This depends on the selected mode:
	 * - Personal: current user id
	 * - Manager: selected user id of combobox
	 * @private
	 */
	private getUserId() {
		switch (this.modeControl.getValue()) {
			case RecordingValidationsMode.Manager:
				return this.userCombobox.getSelectedItem()?.id;
			case RecordingValidationsMode.Personal:
			default:
				// ignore userCombobox-Filter in Personal Mode
				return null;
		}
	}

	private multiSelectOnChange() {
		const categories = $("#categoriesSelect").val() as string[];
		if (Array.isArray(categories)) {
			BrowserStorageSettings.setRecordingValidationsCategories(Constants.GROUP_RECORDING_VALIDATIONS, categories.join(","));
		}
	}
}
